import { StylesheetProvider } from '@botpress/webchat'
import { initialConfig, LatestWebchatTheme } from '@bpinternal/shared'
import { Button, Card } from '@bpinternal/ui-kit-next'
import { MessagesSquare } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { MessageItem } from '~/components/Messages'
import { EmptyState, Icon, Spinner } from '~/elements'
import EmptyConversationLightIcon from '~/assets/empty-conversation-light.svg?react'
import EmptyConversationDarkIcon from '~/assets/empty-conversation-dark.svg?react'
import { useConversationMessages } from '~/features/conversations/hooks'
import { useLocalStore } from '~/stores'
import { cn } from '~/utils'

type MessageListProps = {
  botId: string
  conversationId: string
  workspaceId: string
  messageId?: string
  className?: string
  disableLink?: boolean
}

export const MessageBoard = ({
  botId,
  conversationId,
  workspaceId,
  messageId,
  className,
  disableLink,
}: MessageListProps) => {
  const getPreferredTheme = useLocalStore((s) => s.getPreferredTheme)

  const webchatTheme: LatestWebchatTheme = {
    ...initialConfig,
    radius: 1,
    themeMode: getPreferredTheme(),
  }

  const {
    data: fetchedMessages,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useConversationMessages({
    botId,
    conversationId,
    workspaceId,
  })

  const messages = fetchedMessages?.pages?.flatMap((page) => page.messages).reverse() ?? []

  const scrollableMessagesRef = useRef<HTMLDivElement>(null)
  const scrollToMessageRef = useRef<HTMLDivElement>(null)

  const [initialized, setInitialized] = useState(!messageId)
  const [hasScrolledToMessage, setHasScrolledToMessage] = useState(false)

  useEffect(() => {
    if (messageId && !messages.some((msg) => msg.id === messageId)) {
      void fetchNextPage()
    }
  }, [messageId, messages.length, fetchNextPage])

  useEffect(() => {
    if (!initialized || !messageId || hasScrolledToMessage) return

    const messageElement = scrollToMessageRef.current
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHasScrolledToMessage(true)
    }
  }, [initialized, messageId, hasScrolledToMessage])

  useEffect(() => {
    if ((messageId && messages.some((msg) => msg.id === messageId)) || !hasNextPage) {
      setInitialized(true)
    }
  }, [isFetching, messages.length, messageId, hasNextPage])

  if (!initialized) {
    return (
      <Card className={cn('flex items-center justify-center border-dashed', className)}>
        <EmptyState />
        <div className="flex items-center gap-2 opacity-60">
          <Icon color="gray" size="4" icon={MessagesSquare} />
          <span className="text-muted-foreground">Loading messages...</span>
        </div>
      </Card>
    )
  }

  if (!isFetching && !messages.length) {
    return (
      <Card
        className={cn(
          'flex items-center justify-center rounded-none border-none bg-neutral-50 md:rounded-xl',
          className
        )}
      >
        <EmptyState
          iconSize={6}
          icon={getPreferredTheme() === 'dark' ? EmptyConversationDarkIcon : EmptyConversationLightIcon}
          className="py-16"
          title="No message in conversation"
        />
      </Card>
    )
  }

  return (
    <div ref={scrollableMessagesRef} className={cn('rounded-none bg-neutral-50 md:rounded-xl', className)}>
      <div className="flex flex-col gap-6 px-6 pb-6">
        <StylesheetProvider {...webchatTheme} />
        <Button
          variant="ghost"
          className="mx-auto my-2 w-fit"
          onClick={() => void fetchNextPage()}
          disabled={!hasNextPage || isFetching}
        >
          {isFetching ? <Spinner size="6" /> : hasNextPage ? 'Load more' : 'Start of conversation'}
        </Button>
        {messages?.map((message) => (
          <MessageItem
            disableLink={disableLink}
            ref={message.id === messageId ? scrollToMessageRef : undefined}
            key={message.id}
            {...message}
            selected={messageId === message.id}
            workspaceId={workspaceId}
            botId={botId}
            conversationId={conversationId}
          />
        ))}
      </div>
    </div>
  )
}
